// SubTabManager.js – Nested sub-tab tree navigator + options
import React, { useState } from "react";
import "../styles/SubTabManager.css";

// ─── Recursive tree renderer ────────────────────────────────────────────────
const SubNode = ({
  node,
  depth,
  selectedTabId,
  setSelectedTabId,
  addSubTab,
  renameTab,
  deleteTab,
  updateTabMeta,
}) => {
  const [expanded, setExpanded] = useState(true);
  const isSelected = node.id === selectedTabId;
  const hasSubs = node.Sub && node.Sub.length > 0;

  return (
    <div className={`sub-node depth-${depth}`}>
      <div
        className={`sub-node-row ${isSelected ? "sub-node-selected" : ""}`}
        onClick={() => setSelectedTabId(node.id)}
      >
        {/* Indent guide lines */}
        {Array.from({ length: depth }).map((_, i) => (
          <span key={i} className="indent-line" />
        ))}

        {/* Expand/collapse toggle if has children */}
        <button
          className="expand-btn"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          style={{ visibility: hasSubs ? "visible" : "hidden" }}
        >
          {expanded ? "▾" : "▸"}
        </button>

        {/* Tab title */}
        <span className="sub-node-title">{node.title}</span>

        {/* KPI count badge */}
        {node.kpis?.length > 0 && (
          <span className="kpi-badge">{node.kpis.length} KPI</span>
        )}

        {/* Actions */}
        <div className="sub-node-actions" onClick={(e) => e.stopPropagation()}>
          <button
            title="Add sub-tab"
            className="sn-action-btn add"
            onClick={() => addSubTab(node.id)}
          >
            +
          </button>
          <button
            title="Rename"
            className="sn-action-btn rename"
            onClick={() => renameTab(node.id)}
          >
            ✎
          </button>
          <button
            title="Delete"
            className="sn-action-btn delete"
            onClick={() => deleteTab(node.id)}
          >
            ×
          </button>
        </div>
      </div>

      {/* Meta options – only show when selected */}
      {isSelected && depth > 0 && (
        <div className="sub-node-meta" onClick={(e) => e.stopPropagation()}>
          <label className="meta-option">
            <input
              type="checkbox"
              checked={!!node.showParentKpi}
              onChange={(e) => updateTabMeta(node.id, "showParentKpi", e.target.checked)}
            />
            Show parent KPIs
          </label>
          <label className="meta-option">
            <input
              type="checkbox"
              checked={!!node.showMainWithSub}
              onChange={(e) => updateTabMeta(node.id, "showMainWithSub", e.target.checked)}
            />
            Show alongside parent
          </label>
        </div>
      )}

      {/* Recursive children */}
      {expanded && hasSubs && (
        <div className="sub-children">
          {node.Sub.map((child) => (
            <SubNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedTabId={selectedTabId}
              setSelectedTabId={setSelectedTabId}
              addSubTab={addSubTab}
              renameTab={renameTab}
              deleteTab={deleteTab}
              updateTabMeta={updateTabMeta}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Find the top-level root tab that contains the selectedTabId ─────────────
const findRootTab = (tabs, selectedTabId) => {
  const search = (nodes, targetId) => {
    for (const n of nodes) {
      if (n.id === targetId) return true;
      if (search(n.Sub || [], targetId)) return true;
    }
    return false;
  };
  return tabs.find(
    (t) => t.id === selectedTabId || search(t.Sub || [], selectedTabId)
  );
};

// ─── Main SubTabManager ──────────────────────────────────────────────────────
export default function SubTabManager({
  tabs,
  selectedTabId,
  setSelectedTabId,
  addSubTab,
  renameTab,
  deleteTab,
  updateTabMeta,
}) {
  const rootTab = findRootTab(tabs, selectedTabId);
  if (!rootTab) return null;

  const hasSubs = rootTab.Sub && rootTab.Sub.length > 0;

  return (
    <div className="sub-tab-manager">
      <div className="stm-header">
        <span className="stm-breadcrumb">
          📂 <strong>{rootTab.title}</strong>
        </span>
        <button
          className="stm-add-sub-btn"
          onClick={() => addSubTab(rootTab.id)}
          title="Add sub-tab to this tab"
        >
          + Add Sub-tab
        </button>
      </div>

      {hasSubs && (
        <div className="stm-tree">
          {rootTab.Sub.map((child) => (
            <SubNode
              key={child.id}
              node={child}
              depth={1}
              selectedTabId={selectedTabId}
              setSelectedTabId={setSelectedTabId}
              addSubTab={addSubTab}
              renameTab={renameTab}
              deleteTab={deleteTab}
              updateTabMeta={updateTabMeta}
            />
          ))}
        </div>
      )}

      {!hasSubs && (
        <p className="stm-empty-hint">
          No sub-tabs yet. Click <strong>+ Add Sub-tab</strong> to create one.
        </p>
      )}
    </div>
  );
}